/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo, useRef } from 'react';
import { Button } from 'src/@vymo/ui/atoms';
import { Accordion } from 'src/@vymo/ui/blocks';
import classnames from 'classnames';
import { ReactComponent as SidebarFoldIcon } from 'src/assets/icons/sidebarFold.svg';
import { ReactComponent as SidebarUnfoldIcon } from 'src/assets/icons/sidebarUnfold.svg';
import { ReactComponent as LeadsIcon } from 'src/assets/navigation/leads.svg';
import { isSidebarHidden } from 'src/models/appConfig/selectors';
import { setAppHideConfig } from 'src/models/appConfig/slice';
import { getLmsSessionIdentityKey } from 'src/models/auth/lmsLogin/selector';
import { getSelectedMenu } from 'src/models/lmsConfig/selectors';
import { setSelectedMenu } from 'src/models/lmsConfig/slice';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getWorkspaceRedirectPath } from 'src/workspace/selectors';
import { renderModule } from 'src/workspace/slice';
import { getAppLocationInfo } from 'src/workspace/utils';
import { SIDEBAR_GROUP_ORDER } from './constants';
import { iconsMap } from './icons';
import { getGroupedItems, getMenuV2, queryMenuWithPath } from './queries';
import styles from './index.module.scss';

function Sidebar() {
  const lmsSessionIdentityKey = useAppSelector(getLmsSessionIdentityKey);
  const items = useMemo(() => getMenuV2(), [lmsSessionIdentityKey]); // eslint-disable-line react-hooks/exhaustive-deps -- rebuild when LMS session in Redux changes
  const groupedItems = useMemo(() => getGroupedItems(items), [items]);
  const dispatch = useAppDispatch();
  const selectedMenu = useAppSelector(getSelectedMenu);
  const redirectPath = useAppSelector(getWorkspaceRedirectPath);
  const hideSidebar = useAppSelector(isSidebarHidden);
  const lastPathRef = useRef('__INIT__');

  useEffect(() => {
    const handleRouting = () => {
      const { hash: currentPath } = getAppLocationInfo();

      if (currentPath === lastPathRef.current) return;
      lastPathRef.current = currentPath;

      if (!currentPath || currentPath === '/') {
        dispatch(renderModule({ id: 'HOME_DASHBOARD', props: {} }));
        return;
      }

      const selectedItems = selectedMenu?.menu?.items ?? [];
      const isCurrentPathInSelectedMenu = selectedItems.some(
        (item) => item?.path === currentPath,
      );

      // navigating inside the tabs in the page
      if (isCurrentPathInSelectedMenu) return;

      const { menu, type } = queryMenuWithPath(items, currentPath);
      dispatch(setSelectedMenu({ menu, type } as any));
    };

    handleRouting();
    window.addEventListener('hashchange', handleRouting);
    return () => window.removeEventListener('hashchange', handleRouting);
  }, [dispatch, items, selectedMenu?.menu?.items, redirectPath]);

  return (
    <div
      className={classnames(styles.sidebar, {
        [styles.sidebar__expanded]: !hideSidebar,
        [styles.sidebar__collapsed]: hideSidebar,
      })}
    >
      <Button
        onClick={() =>
          dispatch(setAppHideConfig({ hide: { sidebar: !hideSidebar } }))
        }
        type="text"
        isIcon
        iconProps={{
          icon: !hideSidebar ? <SidebarFoldIcon /> : <SidebarUnfoldIcon />,
          iconPosition: 'left',
        }}
        className={styles.sidebar_toggleIcon}
      />
      <div className={styles.sidebar__groups}>
        {SIDEBAR_GROUP_ORDER.filter(
          (groupKey) => groupedItems[groupKey]?.length,
        ).map((groupKey) => (
          <Accordion
            key={groupKey}
            title={groupKey}
            open
            type="subtle"
            className={classnames(styles.sidebar__groups__header, {
              [styles.sidebar__groups__expanded]: !hideSidebar,
              [styles.sidebar__groups__collapsed]: hideSidebar,
            })}
          >
            {' '}
            <div
              className={classnames(styles.sidebar__items, {
                [styles.sidebar__items__expanded]: !hideSidebar,
                [styles.sidebar__items__collapsed]: hideSidebar,
              })}
            >
              {groupedItems[groupKey]?.map((item) => (
                <div
                  key={item.key}
                  className={classnames(styles.sidebar__item, {
                    [styles.sidebar__item__selected]:
                      selectedMenu?.menu?.key === item.key,
                  })}
                  onClick={() => {
                    dispatch(setSelectedMenu({ menu: item, type: 'direct' }));
                  }}
                >
                  {iconsMap[item.code || item.type] ?? <LeadsIcon />}{' '}
                  {item.name}
                </div>
              ))}
            </div>
          </Accordion>
        ))}
      </div>
    </div>
  );
}

export default Sidebar;
